import { useState, useRef, useCallback, useEffect, isValidElement, ReactNode } from "react";
import { useDispatch } from "react-redux";
import { Button, Col, Form, Input, message, Row, Select, Switch } from "antd";
import { ModalComponent as ModalCompnent, OutPutResult, Spinner } from '@operations/shared';
import { EditOutlined, ShopOutlined, BranchesOutlined, LockOutlined } from "@ant-design/icons";
import regex from '../../utils/helper'
import { getApi, request } from '../../../../shared/src/apis';
import { arabicInputError, englishInputError } from "../../utils/commonStringsConsts";
import { getOnePartnerBranch, getPartners, getPartnersBranches, patchOnePartnerBranch } from "../../utils/apisConsts";
import { FormInstance } from "antd/es/form";
import '../../scss/actionsStyle/_editPartnerBranch.scss';

const { Option } = Select;

export default function EditPartnerBranchForm() {
    const formRef = useRef<FormInstance<any> | null>(null);
    const validation: any = regex.regexValidation;
    const dispatch = useDispatch()
    const [branchData, setBranchData] = useState<any>();
    const [sending, setSending] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMode, setErrorMode] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isDisabled, setIsDisabled] = useState<boolean>();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isPartnersLoading, setIsPartnersLoading] = useState(true);
    const [partnersError, setPartnersError] = useState(false);
    
    const [partners, setPartners] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState<number | undefined>();
    const [selectedBranch, setSelectedBranch] = useState<number | undefined>();
    const [isBranchesLoading, setIsBranchesLoading] = useState(false);
    const [pendingBranchSelection, setPendingBranchSelection] = useState<number | null>(null);
    const [pendingPartnerSelection, setPendingPartnerSelection] = useState<number | null>(null);
    const [pendingAction, setPendingAction] = useState<'branch' | 'partner' | 'clearBranch' | 'clearPartner' | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const toggleHiddenStatus = useCallback((checked: boolean) => {
        setIsDisabled(checked);
        if (formRef.current) {
            formRef.current.setFieldsValue({ isDisabled: checked });
        }
    }, []);

    useEffect(() => {
        setIsDisabled(branchData?.isDisabled === 1 || branchData?.isDisabled === true);
    }, [branchData]);

    const checkForChanges = useCallback(() => {
        if (!formRef.current || !branchData) {
            setHasUnsavedChanges(false);
            return;
        }

        const currentValues = formRef.current.getFieldsValue();
        const hasFormChanges = 
            currentValues.region !== branchData.region ||
            currentValues.region_ar !== branchData.region_ar ||
            currentValues.address !== branchData.address ||
            currentValues.address_ar !== branchData.address_ar ||
            currentValues.locationUrl !== branchData.locationUrl ||
            currentValues.phone !== branchData.phone ||
            isDisabled !== (branchData.isDisabled === 1 || branchData.isDisabled === true);

        setHasUnsavedChanges(hasFormChanges);
    }, [branchData, isDisabled]);

    // Check for changes when isDisabled changes
    useEffect(() => {
        checkForChanges();
    }, [isDisabled, checkForChanges]);

    // Fetch all partners on mount
    const getAllPartners = useCallback(async () => {
        setIsPartnersLoading(true);
        setPartnersError(false);
        try {
            const Partners: any = await dispatch(getApi(getPartners, {}));
            const res = Partners?.payload;

            if (res?.status === 200) {
                setPartners(res?.partners);
            } else {
                setPartnersError(true);
            }
        } catch {
            setPartnersError(true);
        } finally {
            setIsPartnersLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        getAllPartners();
    }, [getAllPartners]);

    // Fetch branches for selected partner
    const getPartnerBranches = useCallback(async (partnerId: number) => {
        setIsBranchesLoading(true);
        
        try {
            const Partners: any = await dispatch(getApi(`${getPartnersBranches}?partner=${partnerId}`, {}));            
            const res = Partners?.payload;

            if (res?.status === 200) {
                setBranches(res?.data?.branches);
            } else {
                message.error(res?.message || 'Failed to load branches', 4);
            }
        } catch {
            message.error('Failed to load branches, please try again', 4);
        } finally {
            setIsBranchesLoading(false);
        }
    }, [dispatch]);

    const getPartnerById = useCallback(async (branchId: number) => {
        if (!branchId) return;
        
        setIsLoading(true);
        setErrorMode(false);
        setBranchData(undefined);
        
        try {
            const apiData: any = await dispatch(request({
                url: `${getOnePartnerBranch}${branchId}`,
                method: "get",
            }));
            
            const res = apiData?.data;
            
            if (res?.status === 200) {
                setBranchData(res?.data);
                setIsLoading(false);
                setHasUnsavedChanges(false);
                
                // Reset form with new data
                if (formRef.current) {
                    formRef.current.resetFields();
                }
                
                return res?.partnerDetails;
            }
            
            setErrorMode(true);
            setIsLoading(false);
            setErrorMessage(res?.message || "Failed to load partner");
        } catch (error) {
            setErrorMode(true);
            setIsLoading(false);
            setErrorMessage("Something went wrong, please try again");
        }
    }, [dispatch]);

    const handleFormChange = useCallback(() => {
        checkForChanges();
    }, [checkForChanges]);

    const handleBranchChange = useCallback((value: number | undefined) => {
        if (!value) {
            // Clear branch
            if (hasUnsavedChanges) {
                setPendingAction('clearBranch');
                setShowConfirmModal(true);
            } else {
                setSelectedBranch(undefined);
                setBranchData(undefined);
                setErrorMode(false);
            }
            return;
        }

        if (hasUnsavedChanges) {
            setPendingBranchSelection(value);
            setPendingAction('branch');
            setShowConfirmModal(true);
        } else {
            setSelectedBranch(value);
            getPartnerById(value);
        }
    }, [hasUnsavedChanges, getPartnerById]);

    const handlePartnerChange = useCallback((value: number | undefined) => {
        if (!value) {
            // Clear partner
            if (hasUnsavedChanges) {
                setPendingAction('clearPartner');
                setShowConfirmModal(true);
            } else {
                setSelectedPartner(undefined);
                setBranches([]);
                setSelectedBranch(undefined);
                setBranchData(undefined);
                setErrorMode(false);
            }
            return;
        }

        if (hasUnsavedChanges) {
            setPendingPartnerSelection(value);
            setPendingAction('partner');
            setShowConfirmModal(true);
        } else {
            setSelectedPartner(value);
            setBranches([]);
            setSelectedBranch(undefined);
            setBranchData(undefined);
            setErrorMode(false);
            getPartnerBranches(value);
        }
    }, [hasUnsavedChanges, getPartnerBranches]);

    const handleConfirmChange = useCallback(() => {
        setHasUnsavedChanges(false);
        
        switch (pendingAction) {
            case 'branch':
                if (pendingBranchSelection !== null) {
                    setSelectedBranch(pendingBranchSelection);
                    getPartnerById(pendingBranchSelection);
                }
                break;
            case 'partner':
                if (pendingPartnerSelection !== null) {
                    setSelectedPartner(pendingPartnerSelection);
                    setBranches([]);
                    setSelectedBranch(undefined);
                    setBranchData(undefined);
                    setErrorMode(false);
                    getPartnerBranches(pendingPartnerSelection);
                }
                break;
            case 'clearBranch':
                setSelectedBranch(undefined);
                setBranchData(undefined);
                setErrorMode(false);
                break;
            case 'clearPartner':
                setSelectedPartner(undefined);
                setBranches([]);
                setSelectedBranch(undefined);
                setBranchData(undefined);
                setErrorMode(false);
                break;
        }
        
        setShowConfirmModal(false);
        setPendingBranchSelection(null);
        setPendingPartnerSelection(null);
        setPendingAction(null);
    }, [pendingAction, pendingBranchSelection, pendingPartnerSelection, getPartnerById, getPartnerBranches]);

    const handleCancelChange = useCallback(() => {
        setShowConfirmModal(false);
        setPendingBranchSelection(null);
        setPendingPartnerSelection(null);
        setPendingAction(null);
    }, []);

    const getOptionText = (node: ReactNode): string => {
        if (typeof node === "string" || typeof node === "number") {
            return String(node);
        }

        if (Array.isArray(node)) {
            return node.map(getOptionText).join(" ");
        }

        if (isValidElement(node)) {
            return getOptionText((node.props as any)?.children);
        }

        return "";
    };

    const dropDownSearch = (input: string, option: any) => {
        const optionText = getOptionText(option?.children).toLowerCase();
        return optionText.includes((input || "").toLowerCase());
    };

    const validateLocationUrl = (_: any, value: string) => {
        if (!value) return Promise.resolve();

        const mapsUrlPattern = /^https?:\/\/(www\.)?(google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|goo\.gl\/maps|maps\.app\.goo\.gl)/i;
        if (!mapsUrlPattern.test(value)) {
            return Promise.reject('Please enter a valid URL');
        }

        return Promise.resolve();
    };

    const submitEditPartnerBranch = async (values: any) => {
        if (!selectedBranch) return;
        
        setSending(true);
        const body = {...values, isDisabled: !!isDisabled};

        try {
            const res: any = await dispatch(request({ url: `${patchOnePartnerBranch}${selectedBranch}`, method: "PATCH", data: body }));
            setSending(false);
            if (res?.data?.status === 200 || res?.data?.status === 201) {
                message.success(res?.data?.message, 4);
                setHasUnsavedChanges(false);
                
                if (selectedPartner) {
                    await getPartnerBranches(selectedPartner);
                }
                
                await getPartnerById(selectedBranch);
            } else {
                message.error(res?.data?.message, 4);
            }
        }
        catch {
            setSending(false);
            message.error('Failed to save changes, please try again', 4);
        }
    };

    const showEditForm = selectedBranch && branchData && !isLoading && !errorMode;

    const selectedBranchName = (branches as any[])?.find((b: any) => b?.id === selectedBranch)?.region || branchData?.region;

    if (isPartnersLoading) {
        return (
            <Spinner size="large" caption="Loading partners..." />
        );
    }

    if (partnersError) {
        return (
            <div className="edit-partner-branch">
                <OutPutResult 
                    status={500} 
                    title="Failed to load partners" 
                    subTitle="Something went wrong while loading partners. Please try again."
                    btnTitle="Retry"
                    btnClick={getAllPartners}
                />
            </div>
        );
    }

    return (
        <div className="edit-partner-branch">
            <ModalCompnent
                title="⚠️ Unsaved Changes"
                visibleModal={showConfirmModal}
                handleAcceptModal={handleConfirmChange}
                handleCloseModal={handleCancelChange}
                okText="Discard Changes"
                cancelText="Keep Editing"
                content={
                    <p style={{ fontSize: '15px', color: '#555', margin: '16px 0' }}>
                        You have unsaved changes that will be lost. Are you sure you want to continue?
                    </p>
                }
            />

            <div className="edit-partner-branch__container">
                <div className="edit-partner-branch__selection-card">
                    <div className="edit-partner-branch__selection-title">
                        <BranchesOutlined style={{ fontSize: '22px' }} />
                        Select Branch to Edit
                    </div>
                    <Row gutter={24}>
                        <Col span={12}>
                            <div className="edit-partner-branch__select-wrapper">
                                <span className="edit-partner-branch__select-label">
                                    <ShopOutlined style={{ marginRight: '6px' }} />
                                    Partner
                                </span>
                                <Select 
                                    style={{ width: '100%' }}
                                    placeholder='Choose a partner...'
                                    showSearch
                                    allowClear
                                    size="large"
                                    loading={isPartnersLoading}
                                    value={selectedPartner}
                                    filterOption={((input, option: any) => dropDownSearch(input, option))}
                                    onChange={handlePartnerChange}
                                >
                                    {partners.map((partner: any) => (
                                        <Option key={partner?.id} value={partner?.id}>{partner?.name}</Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="edit-partner-branch__select-wrapper">
                                <span className="edit-partner-branch__select-label">
                                    <BranchesOutlined style={{ marginRight: '6px' }} />
                                    Branch
                                </span>
                                <Select 
                                    style={{ width: '100%' }}
                                    placeholder={selectedPartner ? 'Choose a branch...' : 'Select partner first'}
                                    disabled={!selectedPartner}
                                    loading={isBranchesLoading}
                                    showSearch
                                    allowClear
                                    size="large"
                                    value={selectedBranch}
                                    filterOption={((input, option: any) => dropDownSearch(input, option))}
                                    onChange={handleBranchChange}
                                >
                                    {branches?.map((branch: any) => (
                                        <Option key={branch?.id} value={branch?.id}>
                                            {branch?.isDisabled === 1 || branch?.isDisabled === true
                                                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><LockOutlined style={{ color: '#faad14' }} />{branch?.region}</span>
                                                : branch?.region
                                            }
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Empty State - when no branch is selected */}
                {!selectedBranch && !isLoading && !errorMode && (
                    <div className="edit-partner-branch__empty-state">
                        <div className="edit-partner-branch__empty-icon">
                            <EditOutlined />
                        </div>
                        <p className="edit-partner-branch__empty-text">No branch selected</p>
                        <p className="edit-partner-branch__empty-subtext">
                            Select a partner and branch above to start editing
                        </p>
                    </div>
                )}

                {isLoading && (
                    <div className="edit-partner-branch__loading">
                <Spinner size={'large'} />
                        <p>Loading branch data...</p>
                    </div>
                )}

                {errorMode && !isLoading && (
                <OutPutResult status={500} title={`Oop's`} subTitle={errorMessage} />
                )}

                {showEditForm && (
                    <div className="edit-partner-branch__form-card" key={selectedBranch}>
                        <div className="edit-partner-branch__form-header">
                            <div className="edit-partner-branch__form-icon">
                                <EditOutlined />
                            </div>
                            <div>
                                <h3 className="edit-partner-branch__form-title">{selectedBranchName}</h3>
                                <p className="edit-partner-branch__form-subtitle">Branch #{branchData?.branchNumber}</p>
                            </div>
                            {hasUnsavedChanges && (
                                <div className="edit-partner-branch__unsaved-badge">
                                    <span>●</span> Unsaved changes
                                </div>
                            )}
                        </div>

                    <Form
                        name="basic"
                        ref={formRef}
                        initialValues={{ remember: true }}
                        onFinish={submitEditPartnerBranch}
                            onValuesChange={handleFormChange}
                        autoComplete="off"
                            layout="vertical"
                        >
                            <Row gutter={[20, 0]}>
                                <Col span={8}>
                                    <div className="edit-partner-branch__field-group edit-partner-branch__field-group--delay-1">
                                        <div className="edit-partner-branch__field-label">Branch Number</div>
                                        <Form.Item name="code" initialValue={branchData?.branchNumber} style={{ marginBottom: 0 }}>
                                            <Input placeholder='Branch number' disabled className="edit-partner-branch__input edit-partner-branch__input--disabled" />
                                </Form.Item>
                                    </div>
                            </Col>
                                <Col span={8}>
                                    <div className="edit-partner-branch__field-group edit-partner-branch__field-group--delay-2">
                                        <div className="edit-partner-branch__field-label">Branch Name <span className="edit-partner-branch__required-star">*</span></div>
                                        <Form.Item name="region" initialValue={branchData?.region} rules={[{ required: true, message: 'Required' }, { pattern: validation.englishRegex, message: englishInputError}]} style={{ marginBottom: 0 }}>
                                            <Input placeholder='Enter branch name' className="edit-partner-branch__input" />
                                </Form.Item>
                                    </div>
                            </Col>
                                <Col span={8}>
                                    <div className="edit-partner-branch__field-group edit-partner-branch__field-group--delay-3">
                                        <div className="edit-partner-branch__field-label">Branch Name (Arabic) <span className="edit-partner-branch__required-star">*</span></div>
                                        <Form.Item name="region_ar" initialValue={branchData?.region_ar} rules={[{ required: true, message: 'Required' }, { pattern: validation.arabicRegex, message: arabicInputError}]} style={{ marginBottom: 0 }}>
                                            <Input placeholder="اسم الفرع بالعربي" className="edit-partner-branch__input" dir="rtl" />
                                </Form.Item>
                                    </div>
                            </Col>
                            </Row>

                            <Row gutter={[20, 0]}>
                                <Col span={8}>
                                    <div className="edit-partner-branch__field-group edit-partner-branch__field-group--delay-4">
                                        <div className="edit-partner-branch__field-label">Phone Number <span className="edit-partner-branch__required-star">*</span></div>
                                        <Form.Item name="phone" initialValue={branchData?.phone} rules={[{ required: true, message: "Required" }, { pattern: validation.phoneNum, message: 'Please enter a valid phone number' }]} style={{ marginBottom: 0 }}>
                                            <Input placeholder='Enter phone number' className="edit-partner-branch__input" />
                                </Form.Item>
                                    </div>
                            </Col>
                                <Col span={16}>
                                    <div className="edit-partner-branch__field-group edit-partner-branch__field-group--delay-5">
                                        <div className="edit-partner-branch__field-label">Maps Location <span className="edit-partner-branch__required-star">*</span></div>
                                        <Form.Item name="locationUrl" initialValue={branchData?.locationUrl} rules={[{ required: true, message: "Required" }, { validator: validateLocationUrl }]} style={{ marginBottom: 0 }}>
                                            <Input placeholder='https://maps.google.com/...' className="edit-partner-branch__input" />
                                </Form.Item>
                                    </div>
                            </Col>
                            </Row>

                            <Row gutter={[20, 0]}>
                            <Col span={12}>
                                    <div className="edit-partner-branch__field-group edit-partner-branch__field-group--delay-6">
                                        <div className="edit-partner-branch__field-label">Address (English) <span className="edit-partner-branch__required-star">*</span></div>
                                        <Form.Item name="address" initialValue={branchData?.address} rules={[{ required: true, message: "Required" }, { pattern: validation.englishRegexAllowNewlines, message: englishInputError }]} style={{ marginBottom: 0 }}>
                                            <Input.TextArea placeholder='Enter address in English' rows={3} className="edit-partner-branch__textarea" />
                                </Form.Item>
                                    </div>
                            </Col>
                            <Col span={12}>
                                    <div className="edit-partner-branch__field-group edit-partner-branch__field-group--delay-7">
                                        <div className="edit-partner-branch__field-label">Address (Arabic) <span className="edit-partner-branch__required-star">*</span></div>
                                        <Form.Item name="address_ar" initialValue={branchData?.address_ar} rules={[{ required: true, message: "Required" }, { pattern: validation.arabicRegex, message: arabicInputError }]} style={{ marginBottom: 0 }}>
                                            <Input.TextArea placeholder='العنوان بالعربي' rows={3} className="edit-partner-branch__textarea" dir="rtl" />
                                </Form.Item>
                                    </div>
                            </Col>
                            </Row>

                            <Row className="edit-partner-branch__footer-row" align="middle">
                            <Col span={12}>
                                    <div className="edit-partner-branch__field-group edit-partner-branch__field-group--delay-8" style={{ marginBottom: 0 }}>
                                        <div className="edit-partner-branch__field-label">Visibility Status</div>
                                        <Form.Item name="isDisabled" valuePropName="checked" initialValue={branchData?.isDisabled === 1 || branchData?.isDisabled === true} style={{ marginBottom: 0 }}>
                                            <div className="edit-partner-branch__visibility-wrapper">
                                    <Switch checked={!!isDisabled} onChange={toggleHiddenStatus} />
                                                <span className={`edit-partner-branch__visibility-text ${isDisabled ? 'edit-partner-branch__visibility-text--hidden' : 'edit-partner-branch__visibility-text--visible'}`}>
                                                    {isDisabled ? 'Hidden' : 'Visible'}
                                                </span>
                                            </div>
                                </Form.Item>
                                    </div>
                            </Col>
                                <Col span={12} style={{ textAlign: 'right' }}>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Button 
                                            htmlType="submit" 
                                            loading={sending}
                                            className="edit-partner-branch__save-button"
                                        >
                                            {sending ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </Form.Item>
                        </Col>
                            </Row>
                    </Form>
                </div>
            )}
            </div>
        </div>
    );
};

